import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useTheme } from '../../hooks/useTheme';
import TradingModeToggle from '../TradingModeToggle';

const Header = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const primaryNavItems = [
    {
      label: 'Dashboard',
      path: '/trading-dashboard-overview',
      icon: 'BarChart3'
    },
    {
      label: 'Live Trading',
      path: '/live-trading-feed',
      icon: 'TrendingUp'
    },
    {
      label: 'AI Oracle',
      path: '/ai-oracle-predictions',
      icon: 'Brain'
    },
    {
      label: 'Multi-Agent',
      path: '/multi-agent-system',
      icon: 'Bot'
    },
    {
      label: 'Voice Control',
      path: '/voice-interface-vamcc',
      icon: 'Mic'
    },
    {
      label: 'Ultra-Fast',
      path: '/ultra-fast-trading',
      icon: 'Zap'
    },
    {
      label: 'Analytics',
      path: '/portfolio-performance-analytics',
      icon: 'PieChart'
    },
    {
      label: 'Risk Assessment',
      path: '/risk-assessment-dashboard',
      icon: 'Shield'
    }
  ];

  const secondaryNavItems = [
    {
      label: 'Market Data Hub',
      path: '/market-data-integration-hub',
      icon: 'Database'
    },
    {
      label: 'Trade Execution',
      path: '/trade-execution-management',
      icon: 'Zap'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <img
              src="/assets/logo.png"
              alt="ASTRA AI Trading"
              className="h-10 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                console.error('Logo failed to load');
              }}
            />
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground tracking-tight">
                ASTRA
              </span>
              <span className="text-xs text-muted-foreground -mt-1">
                AI Trading System
              </span>
            </div>
          </div>
        </div>

        {/* Primary Navigation */}
        <nav className="hidden lg:flex items-center space-x-6">
          {primaryNavItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-smooth focus-ring ${
                isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </button>
          ))}

          {/* More Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              iconName="MoreHorizontal"
              iconSize={16}
              className="text-muted-foreground hover:text-foreground"
            >
              More
            </Button>

            {isMoreMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-popover/95 backdrop-blur-md border border-border rounded-md shadow-elevated z-50">
                <div className="py-2">
                  {secondaryNavItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => {
                        handleNavigation(item?.path);
                        setIsMoreMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-sm transition-smooth hover:bg-muted ${
                        isActive(item?.path)
                          ? 'text-primary font-medium' :'text-popover-foreground'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            iconName="Menu"
            iconSize={20}
            className="text-muted-foreground hover:text-foreground"
          >
          </Button>
        </div>

        {/* User Actions */}
        <div className="hidden lg:flex items-center space-x-3">
          {/* Trading Mode Toggle */}
          <TradingModeToggle />

          <Button
            variant="ghost"
            size="sm"
            iconName="Bell"
            iconSize={16}
            className="text-muted-foreground hover:text-foreground relative"
          >
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-error rounded-full animate-pulse-subtle"></span>
          </Button>
          
          {/* Dark Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            iconName={isDark ? "Sun" : "Moon"}
            iconSize={16}
            className="text-muted-foreground hover:text-foreground"
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            iconSize={16}
            className="text-muted-foreground hover:text-foreground"
          >
          </Button>

          <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
            <Icon name="User" size={16} color="var(--color-muted-foreground)" />
          </div>
        </div>
      </div>
      {/* Mobile Navigation Overlay - Hidden by default */}
      <div className="lg:hidden hidden">
        <div className="bg-card/95 backdrop-blur-md border-t border-border px-6 py-4 space-y-2">
          {[...primaryNavItems, ...secondaryNavItems]?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </button>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;